import { sortedIndexBy } from './array-utils';

/**
 * Basically just a map with faster iteration speeds, decent get speeds, and much shittier
 * set/delete speeds (uses array.splice internally). Also keeps the keys sorted.
 */
export class SortedMap<K, V> implements Pick<Map<K, V>, 'clear' | 'get' | 'has' | 'size'> {
  private array: [K, V][];

  public constructor(entries?: readonly (readonly [K, V])[]) {
    this.array = entries?.map(([k, v]) => [k, v]) ?? [];
  }

  public clear(): void {
    this.array = [];
  }

  public get(key: K): V | undefined {
    return this.getEntry(key, this.sortedIndex(key))?.[1];
  }

  public has(key: K): boolean {
    return !!this.getEntry(key, this.sortedIndex(key));
  }

  public set(key: K, value: V): this {
    const index = this.sortedIndex(key);
    const existing = this.getEntry(key, index);

    if (existing) {
      existing[1] = value;
    }
    else {
      this.array.splice(index, 0, [key, value]);
    }

    return this;
  }

  public get size(): number {
    return this.array.length;
  }

  public entries(): readonly (readonly [K, V])[] {
    return this.array;
  }

  /**
   * Filter in place
   */
  public filter(...params: Parameters<(typeof this.array)['filter']>) {
    this.array = this.array.filter(...params);
  }

  private sortedIndex(key: K) {
    // Binary search
    return sortedIndexBy(this.array, [key, undefined] as [K, V], ([k]) => k);
  }

  private getEntry(key: K, index: number) {
    const result = this.array[index] as [K, V] | undefined; // maybe out of bounds

    return result && result[0] === key ? result : undefined;
  }
}
